import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import type { match } from 'react-router-dom';
import type { History } from 'history';
import { Theme, TextBox, Button, AlertService } from '@spoiler-alert/ui-library';
import client from '../../apollo/client';
import { AdminOrderByExportRunQuery, POLineItem } from './po-queries';
import { VColumn, VTable } from '../../components/v-table';
import routePaths from '../../route-paths';
import type { VTableType } from '../../components/v-table/v-table-types';
import moment from 'moment';
import accounting from 'accounting';
import POManagerDetailLayout from './po-manager-detail-layout';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import DownloadButton from '../numoco-orders/download-button';
import appSettings from '../../app-settings';
import { AdminSubmitPurchaseOrders } from './po-mutations';

const useStyles = createUseStyles({
  wrap: {
    position: 'relative',
    '& h4': {
      margin: 0,
    },
  },
  addresses: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  address: {
    border: `1px solid ${Theme.tableBorderColor}`,
    padding: 10,
  },
  negative: {
    color: Theme.red,
  },
  table: {
    border: `1px solid ${Theme.tableBorderColor}`,
  },
  trucklane: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: [0, 0, 10],
  },
  trucklaneHeading: {
    fontSize: '18px',
    margin: 0,
  },
  siteName: {
    fontSize: '16px',
    margin: 0,
  },
  credit: {
    margin: [10, 0],
    fontSize: 14,
  },
  poEntry: {
    height: 30,
    '& input': {
      textAlign: 'left',
      paddingLeft: 10,
      width: '100%',
    },
  },
});

const AwaitingPODetail: React.FC<{ history: History; match: match<{ exportRunId: string }> }> = ({ match }) => {
  const classes = useStyles();

  const exportRunId = match.params.exportRunId;

  const [poMap, setPOMap] = useState<Map<string, string>>(new Map());

  const { data, loading } = useQuery(AdminOrderByExportRunQuery, { variables: { exportRunId } });

  const [savePOs] = useMutation(AdminSubmitPurchaseOrders);

  const details = data?.adminOrderByExportRunQuery || null;

  useEffect(() => {
    TitleService.setTitles('Sites Administration');
    Breadcrumbs.set([
      {
        url: routePaths.purchaseOrderManager,
        title: 'Purchase Order Administration',
      },
      {
        url: `${routePaths.purchaseOrderManager}/awaiting-po/${exportRunId}`,
        title: 'Order Detail',
      },
    ]);
  }, [exportRunId]);

  useEffect(() => {
    if (details?.lineItems) {
      const _poMap = details.lineItems.reduce((map, item) => {
        map.set(item._id, item.poNumber || '');
        return map;
      }, new Map<string, string>());
      setPOMap(_poMap);
    }
  }, [details]);

  const documents = useMemo(() => {
    if (details) {
      return details.awardSheets.map(({ type, path }) => (
        <div key={path}>
          <DownloadButton
            kind={type}
            path={path}
            downloadPath={`${appSettings.GRAPHQL_SERVER_URL}download/?path=${encodeURIComponent(path)}&source=FTP`}
          />
        </div>
      ));
    }
    return '';
  }, [details]);

  const orderTotal = useMemo(() => {
    if (details?.lineItems) {
      return details.lineItems.reduce((sum, item) => sum + item.totalPrice, 0);
    }
    return 0;
  }, [details?.lineItems]);

  const sellerOrg = useMemo(() => {
    if (details) {
      return details.sellerSites.find((s) => s.siteType === 'ORGANIZATION');
    }
    return null;
  }, [details]);

  const buyerOrg = useMemo(() => {
    if (details) {
      return details.buyerSites.find((s) => s.siteType === 'ORGANIZATION');
    }
    return null;
  }, [details]);

  const middleLeft = useMemo(() => {
    if (details) {
      return (
        <div>
          <h3 className={classes.siteName}>Seller: {sellerOrg?.siteName}</h3>
          <div className={classes.addresses}>
            {details.sellerSites
              .filter((s) => s._id !== sellerOrg?._id)
              .map((s) => (
                <div className={classes.address} key={s._id}>
                  <div>{s.siteName}</div>
                  <div>{s.physicalAddress.streetAddress}</div>
                  <div>
                    {s.physicalAddress.city}, {s.physicalAddress.stateCode} {s.physicalAddress.zipCode}
                  </div>
                  <div>{s.physicalAddress.countryCode}</div>
                </div>
              ))}
          </div>
        </div>
      );
    }
    return '';
  }, [classes, details, sellerOrg]);

  const middleRight = useMemo(() => {
    if (details) {
      return (
        <div>
          <h3 className={classes.siteName}>Buyer: {buyerOrg?.siteName}</h3>
          <div className={classes.addresses}>
            {details.buyerSites.map((s) => (
              <div className={classes.address} key={s._id}>
                <div>{s.siteName}</div>
                <div>{s.physicalAddress.streetAddress}</div>
                <div>
                  {s.physicalAddress.city}, {s.physicalAddress.stateCode} {s.physicalAddress.zipCode}
                </div>
                <div>{s.physicalAddress.countryCode}</div>
              </div>
            ))}
          </div>
          <div className={classes.credit}>
            <div>Credit Limit: {accounting.formatMoney(details.creditLimit)}</div>
            <div className={details.availableCredit - orderTotal > 0 ? '' : classes.negative}>
              Available Credit: {accounting.formatMoney(details.availableCredit)}
            </div>
          </div>
        </div>
      );
    }
    return '';
  }, [classes, buyerOrg, details, orderTotal]);

  const lineItemsMappedByTrucklane = useMemo(() => {
    if (details?.lineItems) {
      return details.lineItems.reduce((trucklaneMap, item) => {
        if (!trucklaneMap.has(item.trucklane)) {
          trucklaneMap.set(item.trucklane, []);
        }
        trucklaneMap.get(item.trucklane)?.push(item);
        return trucklaneMap;
      }, new Map<string, POLineItem[]>());
    }
    return new Map<string, POLineItem[]>();
  }, [details?.lineItems]);

  const posValid = useMemo(() => {
    const lineItems = details?.lineItems || [];
    return lineItems.reduce((prevValid, item) => {
      const populated = poMap.get(item._id) !== '';
      return prevValid && populated;
    }, true);
  }, [poMap, details?.lineItems]);

  const updatePO = useCallback((poNumber, row) => {
    setPOMap((_poMap) => {
      _poMap.set(row._id, poNumber);
      return new Map(_poMap);
    });
  }, []);

  const saveAllPOs = useCallback(async (): Promise<void> => {
    try {
      if (!posValid) throw new Error('Some values are not filled in');
      const lineItems = details?.lineItems || [];
      const poNumbers = lineItems.reduce((poSet, i) => {
        const { _id, externalTransactionId } = i;
        const _poNumber = poMap.get(_id);
        const key = _poNumber?.trim() || '';
        if (key === '') throw new Error(`Missing PO number for ${externalTransactionId}`);
        if (!poSet.has(key)) {
          poSet.set(key, []);
        }
        poSet.get(key)?.push(_id);
        return poSet;
      }, new Map<string, string[]>());
      const result = await savePOs({
        variables: {
          exportRunId,
          poNumbers: [...poNumbers.entries()].map(([poNumber, transactionIds]) => ({
            poNumber,
            transactionIds,
          })),
        },
        refetchQueries: ['adminOrderByExportRunQuery'],
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'adminAssignedPOQuery' });
      if (result.data?.adminSubmitPurchaseOrders.errors && result.data?.adminSubmitPurchaseOrders.errors?.length > 0) {
        throw new Error(result.data?.adminSubmitPurchaseOrders.errors[0].message);
      }
      AlertService.alert({
        type: 'success',
        message: "PO's were saved successfully!",
        autoDismiss: true,
        dismissDelay: 1000,
      });
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: (error as Error).message,
        autoDismiss: false,
        dismissDelay: 1000,
      });
    }
  }, [poMap, details?.lineItems, exportRunId, posValid, savePOs]);

  const actions = useMemo(() => {
    return (
      <div>
        <Button disabled={!posValid} onClick={saveAllPOs}>
          Save PO Numbers
        </Button>
      </div>
    );
  }, [saveAllPOs, posValid]);

  const table = useMemo(() => {
    const columns = [
      new VColumn({
        field: 'description',
        displayName: 'Description',
        sortable: true,
      }),
      new VColumn({
        field: 'itemName',
        displayName: 'ItemName',
        sortable: true,
      }),
      new VColumn({
        field: 'acceptanceDate',
        displayName: 'Acceptance Date',
        sortable: true,
        formatter: (value) => moment(value).format('MMM D, YYYY'),
      }),
      new VColumn({
        field: 'externalTransactionId',
        displayName: 'External Transaction Id',
        sortable: true,
      }),
      new VColumn({
        field: 'unitPrice',
        displayName: 'Unit Price',
        sortable: true,
        formatter: (value) => accounting.formatMoney(value),
      }),
      new VColumn({
        field: 'quantity',
        displayName: 'Quantity',
        sortable: true,
      }),
      new VColumn({
        field: 'totalPrice',
        displayName: 'Total Price',
        sortable: true,
        defaultSort: true,
        defaultSortDirection: 'desc',
        formatter: (value) => accounting.formatMoney(value),
      }),
      new VColumn({
        field: 'statePONumber',
        displayName: 'PO Number',
        sortable: true,
        formatter: (value, row) => (
          <TextBox id={row._id} value={value} onChange={(v: string) => updatePO(v, row)} className={classes.poEntry} />
        ),
      }),
    ];

    const AwaitingPODetailTable = VTable as VTableType<POLineItem>;
    return [...lineItemsMappedByTrucklane.entries()].map(([trucklane, items]) => {
      const data = items.map((i) => {
        const item = { ...i, statePONumber: poMap.get(i._id) || '' };
        return item;
      });
      return (
        <div key={trucklane}>
          <div className={classes.trucklane}>
            <h3 className={classes.trucklaneHeading}>{trucklane}</h3>
          </div>
          <div className={classes.table}>
            <AwaitingPODetailTable cols={columns} data={data} loading={loading} />
          </div>
        </div>
      );
    });
  }, [classes, lineItemsMappedByTrucklane, updatePO, poMap, loading]);

  return (
    <div className={classes.wrap}>
      <POManagerDetailLayout
        loading={loading}
        title={`Pending MM Order from ${buyerOrg?.siteName}`}
        table={table}
        documents={documents}
        actions={actions}
        middleLeft={middleLeft}
        middleRight={middleRight}
      />
    </div>
  );
};

export default AwaitingPODetail;
